﻿/**
 * StrikeGear Theme JavaScript
 * Core functionality for Shopify theme
 */

(function() {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  function debounce(func, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatCurrencyFromCents(cents, currency = 'EUR', locale = document.documentElement.lang || 'nl-NL') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format((Number(cents) || 0) / 100);
  }

  // === HEADER INTERACTIONS ===
  class HeaderInteractions {
    constructor() {
      this.header = $('#shopify-section-header');
      this.mobileMenu = $('.sg-header__mobile-menu');
      this.scrollThreshold = 24;

      this.init();
    }

    init() {
      if (this.header) {
        window.addEventListener('scroll', debounce(() => {
          this.header.classList.toggle('is-scrolled', window.scrollY > this.scrollThreshold);
        }, 20));
      }

      if (this.mobileMenu) {
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            this.mobileMenu.removeAttribute('open');
          }
        });
      }
    }
  }

  // === CART DRAWER ===
  class CartDrawer {
    constructor() {
      this.drawer = $('#cart-drawer');
      this.overlay = $('#cart-overlay');
      this.closeBtn = $('#cart-close');
      this.continueBtn = $('#cart-close-continue');
      this.itemsContainer = $('#cart-items');
      this.footer = $('#cart-footer');
      this.totalEl = $('#cart-total');
      this.emptyMessage = this.drawer?.dataset.emptyMessage || 'Je winkelwagen is leeg';
      this.triggers = $$('[data-cart-trigger]');

      if (this.drawer && this.itemsContainer) {
        this.init();
      }
    }

    init() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      });

      this.closeBtn?.addEventListener('click', () => this.close());
      this.continueBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('sg:cart:open', () => this.open());
      document.addEventListener('sg:cart:refresh', () => this.updateCart());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      this.itemsContainer.addEventListener('click', (event) => {
        const target = event.target.closest('[data-cart-update], [data-cart-remove]');
        if (!target) return;

        if (target.dataset.cartUpdate) {
          const quantity = Number(target.dataset.quantity);
          if (Number.isFinite(quantity)) {
            this.updateQuantity(target.dataset.cartUpdate, quantity);
          }
          return;
        }

        if (target.dataset.cartRemove) {
          this.removeItem(target.dataset.cartRemove);
        }
      });

      this.updateCart();
    }

    async open() {
      await this.updateCart();
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Cart fetch failed');

        const cart = await response.json();
        this.renderCart(cart);
        this.updateCartCount(cart.item_count);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }

    renderCart(cart) {
      if (!this.itemsContainer) return;

      if (!cart.items || cart.items.length === 0) {
        this.itemsContainer.innerHTML = `
          <div class="sg-cart-drawer__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <circle cx="9" cy="20" r="1"></circle>
              <circle cx="17" cy="20" r="1"></circle>
              <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.5L21 8H7"></path>
            </svg>
            <p>${escapeHtml(this.emptyMessage)}</p>
          </div>
        `;
        this.footer?.classList.add('is-hidden');
        return;
      }

      this.itemsContainer.innerHTML = cart.items.map((item) => {
        const imageHtml = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
          : '';

        const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
          : '';

        const minusDisabled = item.quantity <= 1 ? 'disabled' : '';

        return `
          <div class="cart-item" data-line="${item.line}">
            <div class="cart-item__image">${imageHtml}</div>
            <div class="cart-item__details">
              <h3 class="cart-item__title">${escapeHtml(item.product_title)}</h3>
              ${variantHtml}
              <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
              <div class="cart-item__quantity">
                <button type="button" data-cart-update="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="Verlagen" ${minusDisabled}>-</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-update="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Verhogen">+</button>
              </div>
            </div>
            <button class="cart-item__remove" type="button" data-cart-remove="${escapeHtml(item.key)}" aria-label="Verwijderen">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
              </svg>
            </button>
          </div>
        `;
      }).join('');

      if (this.totalEl) {
        this.totalEl.textContent = this.formatMoney(cart.total_price);
      }

      this.footer?.classList.remove('is-hidden');
    }

    async updateQuantity(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity
          })
        });

        if (!response.ok) throw new Error('Cart update failed');

        const cart = await response.json();
        this.renderCart(cart);
        this.updateCartCount(cart.item_count);
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }

    async removeItem(key) {
      await this.updateQuantity(key, 0);
    }

    updateCartCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = String(count);
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    }
  }

  // === ADD TO CART ===
  class AddToCart {
    constructor() {
      this.forms = $$('[data-add-to-cart-form]');
      if (this.forms.length) {
        this.init();
      }
    }

    init() {
      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.handleSubmit(event, form));
      });
    }

    async handleSubmit(event, form) {
      event.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn) return;

      const originalText = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Toevoegen...';

      try {
        const formData = new FormData(form);
        const quantity = Number(formData.get('quantity')) || 1;
        const upsellItems = $$('[data-upsell-variant-id]:checked', form).map((input) => ({
          id: Number(input.dataset.upsellVariantId),
          quantity,
          properties: {
            Upsell: 'Diemaco add-on'
          }
        })).filter((item) => Number.isFinite(item.id));

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        if (upsellItems.length) {
          const upsellResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: upsellItems
            })
          });

          if (!upsellResponse.ok) {
            throw new Error('Failed to add upsell to cart');
          }
        }

        if (window.theme.cart) {
          await window.theme.cart.open();
        }

        this.showNotification(
          upsellItems.length ? 'Producten toegevoegd aan winkelwagen' : 'Product toegevoegd aan winkelwagen',
          'success'
        );
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showNotification('Er ging iets mis. Probeer opnieuw.', 'error');
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }

    showNotification(message, type = 'success') {
      const notification = document.createElement('div');
      notification.className = `notification notification-${type}`;
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 96px;
        right: 20px;
        background: ${type === 'success' ? 'var(--color-forest)' : 'var(--color-error)'};
        color: white;
        padding: 0.875rem 1.25rem;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        z-index: 1000;
        animation: fadeInUp 0.25s ease-out;
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 2600);
    }
  }

  // === HERO ROTATION ===
  class HeroRotator {
    constructor() {
      this.nodes = $$('[data-hero-rotate]');
      if (this.nodes.length) {
        this.init();
      }
    }

    init() {
      this.nodes.forEach((node) => {
        const phrases = (node.dataset.phrases || '')
          .split('|')
          .map((phrase) => phrase.trim())
          .filter(Boolean);

        if (phrases.length < 2) return;

        const duration = Number(node.dataset.duration) || 4200;
        let index = 0;

        setInterval(() => {
          node.classList.add('is-fading');
          setTimeout(() => {
            index = (index + 1) % phrases.length;
            node.textContent = phrases[index];
            node.classList.remove('is-fading');
          }, 220);
        }, duration);
      });
    }
  }

  // === TESTIMONIALS CAROUSEL ===
  class TestimonialsCarousel {
    constructor() {
      this.viewports = $$('[data-testimonials]');
      if (this.viewports.length) {
        this.init();
      }
    }

    init() {
      this.viewports.forEach((viewport) => this.setupInstance(viewport));
    }

    setupInstance(viewport) {
      const track = $('[data-testimonials-track]', viewport);
      const group = $('[data-testimonials-group]', viewport);
      if (!track || !group) return;

      const state = {
        speed: Number(viewport.dataset.speed) || 26,
        offset: 0,
        groupWidth: 0,
        isDragging: false,
        dragStartX: 0,
        dragStartOffset: 0,
        dragEndAt: -Infinity,
        lastTick: performance.now()
      };

      const applyOffset = () => {
        track.style.transform = `translate3d(${state.offset}px, 0, 0)`;
      };

      const normalizeOffset = () => {
        if (!state.groupWidth) return;

        while (state.offset <= -state.groupWidth) {
          state.offset += state.groupWidth;
        }

        while (state.offset > 0) {
          state.offset -= state.groupWidth;
        }
      };

      const updateWidth = () => {
        state.groupWidth = group.getBoundingClientRect().width;
        normalizeOffset();
        applyOffset();
      };

      const onPointerDown = (event) => {
        if (event.button !== 0 && event.pointerType !== 'touch') return;

        state.isDragging = true;
        state.dragStartX = event.clientX;
        state.dragStartOffset = state.offset;
        state.dragEndAt = -Infinity;
        viewport.classList.add('is-dragging');

        try {
          viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          // Ignore unsupported pointer capture errors.
        }
      };

      const onPointerMove = (event) => {
        if (!state.isDragging) return;

        const delta = event.clientX - state.dragStartX;
        state.offset = state.dragStartOffset + delta;
        normalizeOffset();
        applyOffset();
      };

      const onPointerUp = (event) => {
        if (!state.isDragging) return;

        state.isDragging = false;
        state.dragEndAt = performance.now();
        viewport.classList.remove('is-dragging');

        try {
          viewport.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Ignore unsupported pointer capture errors.
        }
      };

      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerUp);
      viewport.addEventListener('pointercancel', onPointerUp);
      viewport.addEventListener('pointerleave', onPointerUp);

      window.addEventListener('resize', debounce(updateWidth, 120));
      updateWidth();

      const tick = (now) => {
        const deltaMs = now - state.lastTick;
        state.lastTick = now;

        if (!state.isDragging && now - state.dragEndAt >= 2000 && state.groupWidth > 0) {
          state.offset -= (state.speed * deltaMs) / 1000;
          normalizeOffset();
          applyOffset();
        }

        window.requestAnimationFrame(tick);
      };

      window.requestAnimationFrame(tick);
    }
  }

  // === PRODUCT DETAIL INTERACTIONS ===
  class ProductDetailInteractions {
    constructor() {
      this.roots = $$('[data-product-detail]');
      if (this.roots.length) {
        this.init();
      }
    }

    init() {
      this.roots.forEach((root) => {
        this.setupVariantSync(root);
        this.setupGallery(root);
      });
    }

    setupVariantSync(root) {
      const select = $('[data-variant-select]', root);
      const priceEl = $('[data-product-price]', root);
      const submitBtn = $('[data-product-submit]', root);
      if (!select || !priceEl) return;

      const availableLabel = submitBtn?.dataset.availableLabel || 'Toevoegen aan winkelwagen';
      const unavailableLabel = submitBtn?.dataset.unavailableLabel || 'Tijdelijk niet op voorraad';

      const syncVariantUI = () => {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        const price = Number(option.dataset.price || 0);
        const currency = option.dataset.currency || 'EUR';
        const available = option.dataset.available === 'true';

        if (price > 0) {
          priceEl.textContent = formatCurrencyFromCents(price, currency);
        } else {
          priceEl.textContent = 'Prijs op maat';
        }

        if (submitBtn) {
          submitBtn.disabled = !available;
          submitBtn.textContent = available ? availableLabel : unavailableLabel;
        }
      };

      select.addEventListener('change', syncVariantUI);
      syncVariantUI();
    }

    setupGallery(root) {
      const mainImage = $('[data-product-main-image]', root);
      const thumbs = $$('[data-product-thumb]', root);
      if (!mainImage || !thumbs.length || mainImage.tagName !== 'IMG') return;

      const prevBtn = $('[data-product-prev]', root);
      const nextBtn = $('[data-product-next]', root);
      let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));

      const applyImage = (index) => {
        const target = thumbs[index];
        if (!target) return;

        const src = target.dataset.imageSrc;
        const alt = target.dataset.imageAlt || mainImage.getAttribute('alt') || '';
        if (!src) return;

        mainImage.setAttribute('src', src);
        mainImage.setAttribute('alt', alt);

        thumbs.forEach((thumb, thumbIndex) => {
          thumb.classList.toggle('is-active', thumbIndex === index);
        });

        activeIndex = index;
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => applyImage(index));
      });

      prevBtn?.addEventListener('click', () => {
        const nextIndex = (activeIndex - 1 + thumbs.length) % thumbs.length;
        applyImage(nextIndex);
      });

      nextBtn?.addEventListener('click', () => {
        const nextIndex = (activeIndex + 1) % thumbs.length;
        applyImage(nextIndex);
      });
    }
  }

  // === GALLERY LIGHTBOX ===
  class GalleryLightbox {
    constructor() {
      this.galleries = $$('[data-gallery]');
      this.modal = null;
      this.imageEl = null;
      this.items = [];
      this.index = 0;

      if (this.galleries.length) {
        this.init();
      }
    }

    init() {
      this.buildModal();
      this.galleries.forEach((gallery) => this.bindGallery(gallery));
    }

    buildModal() {
      const modal = document.createElement('div');
      modal.className = 'sg-lightbox';
      modal.setAttribute('aria-hidden', 'true');
      modal.innerHTML = `
        <div class="sg-lightbox__frame" role="dialog" aria-modal="true" aria-label="Galerij">
          <button type="button" class="sg-lightbox__close" aria-label="Sluiten">&times;</button>
          <button type="button" class="sg-lightbox__prev" aria-label="Vorige">&lt;</button>
          <img class="sg-lightbox__image" src="" alt="">
          <button type="button" class="sg-lightbox__next" aria-label="Volgende">&gt;</button>
        </div>
      `;

      document.body.appendChild(modal);
      this.modal = modal;
      this.imageEl = $('.sg-lightbox__image', modal);

      $('.sg-lightbox__close', modal)?.addEventListener('click', () => this.close());
      $('.sg-lightbox__prev', modal)?.addEventListener('click', () => this.show(this.index - 1));
      $('.sg-lightbox__next', modal)?.addEventListener('click', () => this.show(this.index + 1));

      modal.addEventListener('click', (event) => {
        if (event.target === modal) {
          this.close();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (!this.modal?.classList.contains('is-open')) return;

        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
      });
    }

    bindGallery(gallery) {
      const items = $$('[data-gallery-item]', gallery);
      if (!items.length) return;

      items.forEach((item, itemIndex) => {
        item.addEventListener('click', () => {
          this.items = items;
          this.open(itemIndex);
        });
      });
    }

    open(index = 0) {
      if (!this.modal || !this.imageEl || !this.items.length) return;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.show(index);
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    show(nextIndex) {
      if (!this.items.length || !this.imageEl) return;

      const wrapped = (nextIndex + this.items.length) % this.items.length;
      const item = this.items[wrapped];
      const src = item.dataset.imageSrc;
      const alt = item.dataset.imageAlt || '';
      if (!src) return;

      this.imageEl.setAttribute('src', src);
      this.imageEl.setAttribute('alt', alt);
      this.index = wrapped;
    }
  }

  // === LAZY EMBEDS ===
  class LazyEmbeds {
    constructor() {
      this.embeds = $$('[data-lazy-embed]');

      if (this.embeds.length) {
        this.init();
      }
    }

    init() {
      this.embeds.forEach((embed) => {
        $('[data-lazy-embed-trigger]', embed)?.addEventListener('click', () => {
          this.load(embed);
        });
      });

      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.load(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      );

      this.embeds.forEach((embed) => observer.observe(embed));
    }

    load(embed) {
      if (!embed || embed.dataset.lazyEmbedLoaded === 'true') return;

      const template = $('[data-lazy-embed-template]', embed);
      const placeholder = $('[data-lazy-embed-placeholder]', embed);
      if (!template) return;

      embed.dataset.lazyEmbedLoaded = 'true';

      if (placeholder) {
        placeholder.innerHTML = '<p class="sg-gallery__embed-kicker">Instagram</p><h2>Feed wordt geladen</h2>';
      }

      const fragment = template.content.cloneNode(true);
      const scripts = $$('script', fragment);
      scripts.forEach((script) => script.remove());

      if (placeholder) {
        placeholder.replaceWith(fragment);
      } else {
        embed.appendChild(fragment);
      }

      scripts.forEach((script) => {
        const nextScript = document.createElement('script');
        Array.from(script.attributes).forEach((attr) => {
          nextScript.setAttribute(attr.name, attr.value);
        });
        nextScript.textContent = script.textContent;
        document.body.appendChild(nextScript);
      });
    }
  }

  // === COOKIE CONSENT ===
  class CookieConsent {
    constructor() {
      this.banner = $('#cookie-banner');
      this.storageKey = this.banner?.dataset.storageKey || 'sg_cookie_consent';
      this.gaId = this.banner?.dataset.gaId || '';

      if (this.banner) {
        this.init();
      }
    }

    init() {
      const status = localStorage.getItem(this.storageKey);

      if (status === 'granted') {
        this.hide();
        this.loadAnalytics();
        return;
      }

      if (status === 'denied') {
        this.hide();
        return;
      }

      this.show();

      $('[data-cookie-accept]', this.banner)?.addEventListener('click', () => {
        this.persist('granted');
        this.loadAnalytics();
        this.hide();
      });

      $('[data-cookie-decline]', this.banner)?.addEventListener('click', () => {
        this.persist('denied');
        this.hide();
      });
    }

    show() {
      this.banner?.classList.remove('is-hidden');
    }

    hide() {
      this.banner?.classList.add('is-hidden');
    }

    persist(value) {
      localStorage.setItem(this.storageKey, value);
      document.cookie = `sg_cookie_consent=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
    }

    loadAnalytics() {
      if (!this.gaId || document.querySelector(`script[data-gtag="${this.gaId}"]`)) return;

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
      script.setAttribute('data-gtag', this.gaId);
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      const gtag = (...args) => window.dataLayer.push(args);
      gtag('js', new Date());
      gtag('config', this.gaId, { anonymize_ip: true });
    }
  }

  // === SCROLL TO TOP ===
  class ScrollToTop {
    constructor() {
      this.btn = $('#scroll-to-top');

      if (this.btn) {
        this.init();
      }
    }

    init() {
      window.addEventListener('scroll', debounce(() => {
        this.btn.classList.toggle('is-visible', window.scrollY > 500);
      }, 100));

      this.btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  // === FORM VALIDATION ===
  class FormValidation {
    constructor() {
      this.forms = $$('form[data-validate], form#ContactForm');
      if (this.forms.length) {
        this.init();
      }
    }

    init() {
      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.validate(event, form));
      });
    }

    validate(event, form) {
      let isValid = true;
      const inputs = form.querySelectorAll('[required]');

      inputs.forEach((input) => {
        if (!input.value.trim()) {
          isValid = false;
          this.showError(input, 'Dit veld is verplicht');
        } else if (input.type === 'email' && !this.isValidEmail(input.value)) {
          isValid = false;
          this.showError(input, 'Vul een geldig e-mailadres in');
        } else {
          this.clearError(input);
        }
      });

      if (!isValid) {
        event.preventDefault();
      }
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    showError(input, message) {
      const existing = input.parentElement.querySelector('.error-message');
      const error = existing || this.createError();

      if (!existing) {
        input.parentElement.appendChild(error);
      }

      error.textContent = message;
      input.classList.add('has-error');
    }

    clearError(input) {
      const error = input.parentElement.querySelector('.error-message');
      if (error) {
        error.remove();
      }
      input.classList.remove('has-error');
    }

    createError() {
      const error = document.createElement('span');
      error.className = 'error-message';
      error.style.cssText = 'color: var(--color-error); font-size: var(--text-sm); margin-top: var(--spacing-1); display: block;';
      return error;
    }
  }

  // === INITIALIZE ===
  window.theme = window.theme || {};

  document.addEventListener('DOMContentLoaded', () => {
    window.theme.headerInteractions = new HeaderInteractions();
    window.theme.cart = new CartDrawer();
    window.theme.addToCart = new AddToCart();
    window.theme.heroRotator = new HeroRotator();
    window.theme.testimonialsCarousel = new TestimonialsCarousel();
    window.theme.productDetailInteractions = new ProductDetailInteractions();
    window.theme.galleryLightbox = new GalleryLightbox();
    window.theme.lazyEmbeds = new LazyEmbeds();
    window.theme.cookieConsent = new CookieConsent();
    window.theme.scrollToTop = new ScrollToTop();
    window.theme.formValidation = new FormValidation();
  });
})();
